import React, { memo, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  translateStoreCategory,
  localizedStoreProductName,
  isStoreArabicLang,
  isStoreProductOutOfStock,
  getStoreProductPriceDisplay,
} from "../../../utils/storeUtils";
import { StoreProductPriceBlock } from "./StoreProductPriceBlock";
import BackgroundGradient from "./BackgroundGradient";

function ProductCard({
  product,
  onAddToCart,
  resolveUrl,
  animationDelay = 0,
  onToggleWishlist,
  isInWishlist,
  onQuickView,
}) {
  const { t, i18n } = useTranslation("global");
  const isRtl = (i18n.language || "").toLowerCase().startsWith("ar");
  const [selectedColor, setSelectedColor] = useState(null);
  const [galleryIndex, setGalleryIndex] = useState(0);

  const rating = product?.rating ?? 5.0;
  const images = useMemo(() => {
    const list = product?.images?.length ? product.images : (product?.image?.url ? [product.image] : []);
    return list;
  }, [product]);
  const colors = product?.colors || [];
  const disableHoverImageSwap = colors.length > 0;
  const hasMultipleImages = images.length > 1;
  const currentImage = images[galleryIndex];
  const mainUrl = currentImage ? (resolveUrl?.(currentImage?.url) ?? currentImage?.url) ?? "" : "";
  const productTitle = localizedStoreProductName(product, i18n.language);
  const mainAlt =
    (isStoreArabicLang(i18n.language)
      ? currentImage?.arAlt || currentImage?.alt
      : currentImage?.alt || currentImage?.arAlt) ||
    productTitle ||
    t("store.checkout.productAlt", { defaultValue: "Product" });
  const secondUrl =
    !disableHoverImageSwap && images[1]
      ? (resolveUrl?.(images[1]?.url) ?? images[1]?.url)
      : "";
  const secondAlt =
    (isStoreArabicLang(i18n.language)
      ? images[1]?.arAlt || images[1]?.alt
      : images[1]?.alt || images[1]?.arAlt) ||
    productTitle ||
    t("store.checkout.productAlt", { defaultValue: "Product" });
  const effectiveColorName = selectedColor ?? (colors[0]?.name || null);
  const effectiveColor = colors.find((c) => c.name === effectiveColorName) || null;
  const getSwatchBackground = (colorEntry) => {
    if (!colorEntry) return "#ccc";
    const hexesRaw = Array.isArray(colorEntry.hexes) ? colorEntry.hexes : [];
    const hexes = hexesRaw.map((h) => String(h || "").trim()).filter(Boolean);
    const fallback = String(colorEntry.hex || "").trim();
    const list = hexes.length > 0 ? hexes : fallback ? [fallback] : [];
    if (list.length <= 1) return list[0] || "#ccc";
    const step = 100 / list.length;
    const stops = list
      .map((hex, idx) => `${hex} ${Math.floor(idx * step)}% ${Math.floor((idx + 1) * step)}%`)
      .join(", ");
    return `conic-gradient(from 90deg, ${stops})`;
  };
  const findColorImageIndex = (colorEntry) => {
    if (!colorEntry || images.length === 0) return 0;
    const colorImageIndex = Number(colorEntry.imageIndex);
    if (
      Number.isInteger(colorImageIndex) &&
      colorImageIndex >= 0 &&
      colorImageIndex < images.length
    ) {
      return colorImageIndex;
    }
    if (colorEntry.imageUrl) {
      const matchedIndex = images.findIndex((img) => img?.url === colorEntry.imageUrl);
      if (matchedIndex >= 0) return matchedIndex;
    }
    return 0;
  };

  const outOfStock = isStoreProductOutOfStock(product);
  const priceDisplay = useMemo(
    () => getStoreProductPriceDisplay(product),
    [product]
  );
  const addToCartLabel = outOfStock
    ? t("store.outOfStock", { defaultValue: "Out of stock" })
    : t("store.addToCart", { defaultValue: "Add to cart" });

  const handleAddToCart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (outOfStock) return;
    onAddToCart(product, {
      selectedColor: colors.length > 0 ? effectiveColorName : undefined,
      selectedColorHex: effectiveColor?.hexes?.[0] || effectiveColor?.hex || undefined,
    });
  };

  const handlePrev = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setGalleryIndex((i) =>
      isRtl ? (i >= images.length - 1 ? 0 : i + 1) : i <= 0 ? images.length - 1 : i - 1
    );
  };
  const handleNext = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setGalleryIndex((i) =>
      isRtl ? (i <= 0 ? images.length - 1 : i - 1) : i >= images.length - 1 ? 0 : i + 1
    );
  };

  const detailUrl = `/store/product/${product?._id}`;

  return (
    <BackgroundGradient
      containerClassName="store-card-gradient-shell"
      className="store-card-gradient-content"
      animate={false}
    >
      <Link
        to={detailUrl}
        className="store-card"
        style={{ animationDelay: `${animationDelay}ms` }}
        dir={isRtl ? "rtl" : "ltr"}
        lang={isRtl ? "ar" : "en"}
      >
        <div className="store-card-img-wrap">
          {priceDisplay.hasDiscount ? (
            <span className="store-card-discount-badge">
              {t("store.discountPercentBadge", {
                percent: priceDisplay.discountPercent,
                defaultValue: "-{{percent}}%",
              })}
            </span>
          ) : null}
          <div className="store-card-img-inner">
            <img
              className="store-card-img-main"
              src={mainUrl}
              alt={mainAlt}
              loading="lazy"
              style={{ objectFit: "cover", objectPosition: "center" }}
            />
            {secondUrl && galleryIndex === 0 && (
              <img
                className="store-card-img-hover"
                src={secondUrl}
                alt={secondAlt}
                style={{ objectFit: "cover", objectPosition: "center" }}
              />
            )}
          </div>
          {hasMultipleImages && (
            <>
              <button
                type="button"
                className="store-card-arrow store-card-arrow-prev"
                onClick={handlePrev}
                aria-label={t("store.productImagePrev", {
                  defaultValue: "Previous image",
                })}
              >
                <i
                  className={`fa-solid ${
                    isRtl ? "fa-chevron-right" : "fa-chevron-left"
                  }`}
                  aria-hidden="true"
                />
              </button>
              <button
                type="button"
                className="store-card-arrow store-card-arrow-next"
                onClick={handleNext}
                aria-label={t("store.productImageNext", {
                  defaultValue: "Next image",
                })}
              >
                <i
                  className={`fa-solid ${
                    isRtl ? "fa-chevron-left" : "fa-chevron-right"
                  }`}
                  aria-hidden="true"
                />
              </button>
            </>
          )}
          {onToggleWishlist && (
            <button
              type="button"
              className={`store-card-wishlist${isInWishlist?.(product?._id) ? " is-active" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onToggleWishlist(product);
              }}
              aria-label={
                isInWishlist?.(product?._id)
                  ? t("store.wishlistRemove", { defaultValue: "Remove from wishlist" })
                  : t("store.wishlistAdd", { defaultValue: "Add to wishlist" })
              }
            >
              <i className={isInWishlist?.(product?._id) ? "fa-solid fa-heart" : "fa-regular fa-heart"} />
            </button>
          )}
        </div>

      <div className="store-card-info">
        <h4 className="store-card-title">{productTitle}</h4>
        {product?.category && (
          <p className="store-card-category">
            {translateStoreCategory(t, { label: product.category })}
          </p>
        )}
        <div className="store-card-price-row">
          <span className="store-price store-price--product-card">
            <StoreProductPriceBlock product={product} showDiscountPercent={false} />
          </span>
          <span className="store-card-rating">
            {Number(rating).toFixed(1)} <i className="fa-solid fa-star store-card-star" />
          </span>
        </div>
      </div>

      {colors.length > 0 && (
        <div className="store-card-colors" onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}>
          <span className="store-card-colors-label">
            {t("store.productColorLabel", { defaultValue: "Color:" })}
          </span>
          {colors.map((c) => (
            <button
              key={c.name}
              type="button"
              className={`store-card-color-swatch${effectiveColorName === c.name ? " selected" : ""}`}
              data-color-name={c.name}
              style={{ background: getSwatchBackground(c) }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSelectedColor(c.name);
                setGalleryIndex(findColorImageIndex(c));
              }}
              aria-label={t("store.productCardColorOption", {
                name: c.name,
                defaultValue: `Color ${c.name}`,
              })}
            />
          ))}
        </div>
      )}

      <div className="store-card-actions">
        <button
          type="button"
          className={`store-card-add-btn${outOfStock ? " store-card-add-btn--disabled" : ""}`}
          onClick={handleAddToCart}
          disabled={outOfStock}
          title={
            outOfStock
              ? t("store.outOfStock", { defaultValue: "Out of stock" })
              : undefined
          }
        >
          <span className="store-card-add-btn-base">
            <span className="store-card-add-btn-text">{addToCartLabel}</span>
          </span>
          <span className="store-card-add-btn-hover" aria-hidden="true">
            <i className="fa-solid fa-cart-shopping" />
          </span>
        </button>
        <button
          type="button"
          className="store-card-action-icon"
          data-tooltip={t("store.quickView", { defaultValue: "Quick view" })}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onQuickView?.(product);
          }}
          aria-label={t("store.quickView", { defaultValue: "Quick view" })}
        >
          <i className="fa-solid fa-magnifying-glass" />
        </button>
      </div>
      </Link>
    </BackgroundGradient>
  );
}

export default memo(ProductCard);
